// SendProposalModal.tsx — pick a template, fill the blanks, check the preview, send.
// Opened from the Proposals card on the lead page.
//
// ROLES — admin and staff send exactly the same way (the API allows both).
// The only difference is the empty state when there are no active templates:
//   admin → a button that opens the template manager (/proposals)
//   staff → "ask your admin" (staff can't create templates)
//
// Flow:  GET templates (active) → choose → POST …/preview (live, debounced)
//        → POST …/proposals.
//   email     the server sends it, then the modal closes
//   whatsapp  the server saves it and returns a wa.me link; the modal shows an
//             OPEN WHATSAPP button (a click is needed — browsers block pop-ups
//             opened after a network call)

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, FileText, Loader2, Mail, MessageCircle, Paperclip, Send, X } from 'lucide-react';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalDateField } from '../../../components/GlobalComponents/GlobalDateField';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import ProposalDialog, { FIELD_LABEL, Notice } from '../../Proposals/parts/ProposalDialog';
import {
  FALLBACK_AUTO_VARIABLES,
  FIELD_VARIABLES,
  Proposal,
  ProposalPreview,
  ProposalRequest,
  ProposalTemplate,
  apiError,
  apiMissing,
  dateKey,
  fmtINR,
  proposalApi,
  useProposalRole,
  varLabel,
} from '../../Proposals/parts/proposalTypes';

interface SendProposalModalProps {
  visible: boolean;
  onHide: () => void;
  leadId: string;
  leadName: string;
  /** template chosen in the templates sidebar — preselected once the list has loaded */
  initialTemplateId?: string | null;
  /** resend: amount / valid-till copied from the earlier proposal ('YYYY-MM-DD' or ISO) */
  initialAmount?: number | null;
  initialValidTill?: string | null;
  /** called as soon as the proposal is saved — the page reloads the lead (timeline) and the list */
  onSent: (p: Proposal) => void;
}

const PREVIEW_DEBOUNCE_MS = 400;
const WHATSAPP_AUTO_OPEN_SECONDS = 5;

/** "" → null (not given) · "1,500.50" → 1500.5 · garbage / ≤ 0 → 'invalid' */
const parseAmount = (s: string): number | null | 'invalid' => {
  const t = s.trim().replace(/,/g, '');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? n : 'invalid';
};

const CHANNEL_ICON = { email: Mail, whatsapp: MessageCircle };

const SendProposalModal: React.FC<SendProposalModalProps> = ({ visible, onHide, leadId, leadName, initialTemplateId, initialAmount, initialValidTill, onSent }) => {
  const navigate = useNavigate();
  const { isAdmin } = useProposalRole();

  // templates
  const [templates, setTemplates] = useState<ProposalTemplate[]>([]);
  const [autoVariables, setAutoVariables] = useState<string[]>(FALLBACK_AUTO_VARIABLES);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tplQuery, setTplQuery] = useState('');

  // the form
  const [templateId, setTemplateId] = useState('');
  const [vars, setVars] = useState<Record<string, string>>({});
  const [extraNames, setExtraNames] = useState<string[]>([]); // auto placeholders this lead couldn't fill
  const [amount, setAmount] = useState('');
  const [validTill, setValidTill] = useState<Date | null>(null);

  // preview / send
  const [preview, setPreview] = useState<ProposalPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [whatsappUrl, setWhatsappUrl] = useState<string | null>(null); // set once a WhatsApp proposal is saved

  const [countdown, setCountdown] = useState<number | null>(null); // seconds until WhatsApp opens by itself

  const seq = useRef(0); // ignore preview answers that arrive out of order

  const selected = templates.find((t) => t.id === templateId) ?? null;
  const parsedAmount = parseAmount(amount);
  const amountInvalid = parsedAmount === 'invalid';

  // ── Reset + load templates every time the modal opens ──────────────────────
  useEffect(() => {
    if (!visible) return;
    setTemplateId('');
    setVars({});
    setExtraNames([]);
    setAmount('');
    setValidTill(null);
    setPreview(null);
    setPreviewError(null);
    setSendError(null);
    setWhatsappUrl(null);
    setTplQuery('');

    let cancelled = false;
    (async () => {
      setLoadingTemplates(true);
      setLoadError(null);
      try {
        // active only — the server sends staff only active ones anyway
        const res = await proposalApi.templates.list({ is_active: true });
        if (cancelled) return;
        const list = res.data ?? [];
        setTemplates(list);
        if (initialTemplateId && list.some((t) => t.id === initialTemplateId)) {
          setTemplateId(initialTemplateId);
          if (typeof initialAmount === 'number' && initialAmount > 0) setAmount(String(initialAmount));
          const m = initialValidTill?.match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (m) {
            const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            // only carry the date over if it is still in the future
            if (d.getTime() >= new Date().setHours(0, 0, 0, 0)) setValidTill(d);
          }
        }
        if (res.auto_variables?.length) setAutoVariables(res.auto_variables);
      } catch (e) {
        if (!cancelled) setLoadError(apiError(e, 'Could not load the templates.'));
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // ── WhatsApp step: count down, then open the chat automatically ────────────
  useEffect(() => {
    if (!whatsappUrl || !visible) {
      setCountdown(null);
      return;
    }
    setCountdown(WHATSAPP_AUTO_OPEN_SECONDS);
    const id = setInterval(() => setCountdown((c) => (c === null ? null : c - 1)), 1000);
    return () => clearInterval(id);
  }, [whatsappUrl, visible]);

  useEffect(() => {
    if (countdown !== 0 || !whatsappUrl) return;
    // a timer is not a click, so a pop-up blocker may stop this — the button stays as the fallback
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    setCountdown(null);
  }, [countdown, whatsappUrl]);

  const pickTemplate = (id: string) => {
    if (id === templateId) return;
    setTemplateId(id);
    setVars({});
    setExtraNames([]);
    setAmount('');
    setValidTill(null);
    setPreview(null);
    setPreviewError(null);
    setSendError(null);
  };

  const buildRequest = (): ProposalRequest => {
    const variables: Record<string, string> = {};
    Object.entries(vars).forEach(([k, v]) => {
      if (v.trim()) variables[k] = v.trim();
    });
    const req: ProposalRequest = { template_id: templateId };
    if (Object.keys(variables).length) req.variables = variables;
    if (typeof parsedAmount === 'number') req.amount = parsedAmount;
    if (validTill) req.valid_till = dateKey(validTill);
    return req;
  };

  // ── Live preview (debounced) ───────────────────────────────────────────────
  const varsKey = JSON.stringify(vars);
  useEffect(() => {
    if (!visible || !templateId || amountInvalid || whatsappUrl) {
      setPreviewing(false);
      return;
    }
    const mine = ++seq.current;
    setPreviewing(true);
    const timer = setTimeout(async () => {
      try {
        const res = await proposalApi.lead.preview(leadId, buildRequest());
        if (mine !== seq.current) return;
        setPreview(res.data);
        setPreviewError(null);
        // keep an input for every placeholder the lead couldn't fill, even after it gets a value
        const asked = res.data.missing.filter((m) => !FIELD_VARIABLES.includes(m));
        if (asked.length) setExtraNames((cur) => Array.from(new Set([...cur, ...asked])));
      } catch (e) {
        if (mine !== seq.current) return;
        setPreview(null);
        setPreviewError(apiError(e, 'Could not build the preview.'));
      } finally {
        if (mine === seq.current) setPreviewing(false);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, templateId, varsKey, amount, validTill, leadId, whatsappUrl]);

  // ── Inputs that need to be shown for this template ─────────────────────────
  const customNames = useMemo(() => {
    if (!selected) return [];
    const names = selected.variables.filter((v) => !autoVariables.includes(v) && !FIELD_VARIABLES.includes(v));
    extraNames.forEach((n) => {
      if (!names.includes(n)) names.push(n);
    });
    return names;
  }, [selected, autoVariables, extraNames]);

  const usesAmount = !!selected?.variables.includes('amount');
  const usesValidTill = !!selected?.variables.includes('valid_till');

  const missing = preview?.missing ?? [];
  const canSend = !!preview && missing.length === 0 && !previewing && !amountInvalid && !sending && !previewError;

  // ── Send ───────────────────────────────────────────────────────────────────
  const send = async () => {
    if (!canSend || !selected) return;
    setSending(true);
    setSendError(null);
    try {
      const res = await proposalApi.lead.send(leadId, buildRequest());
      onSent(res.data);
      if (res.whatsapp_url) {
        setWhatsappUrl(res.whatsapp_url); // show the OPEN WHATSAPP step
      } else {
        onHide();
      }
    } catch (e) {
      const still = apiMissing(e);
      setSendError(still.length ? `Still needed: ${still.map(varLabel).join(', ')}.` : apiError(e, 'Could not send the proposal.'));
    } finally {
      setSending(false);
    }
  };

  // ── Pieces ─────────────────────────────────────────────────────────────────
  const grouped = useMemo(() => {
    const q = tplQuery.trim().toLowerCase();
    const map = new Map<string, ProposalTemplate[]>();
    templates
      .filter((t) => !q || `${t.name} ${t.category?.name ?? ''}`.toLowerCase().includes(q))
      .forEach((t) => {
        const key = t.category?.name ?? 'Other';
        map.set(key, [...(map.get(key) ?? []), t]);
      });
    return Array.from(map.entries());
  }, [templates, tplQuery]);

  const templateCard = (t: ProposalTemplate) => {
    const on = t.id === templateId;
    const Icon = CHANNEL_ICON[t.channel];
    return (
      <button
        key={t.id}
        type="button"
        onClick={() => pickTemplate(t.id)}
        disabled={sending}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '9px',
          padding: '8px 10px',
          borderRadius: '8px',
          border: `1.5px solid ${on ? C.teal : G.line}`,
          background: on ? 'rgba(17,85,80,0.05)' : C.white,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ width: 26, height: 26, borderRadius: '7px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: t.channel === 'email' ? C.limeDim : 'rgba(37,211,102,0.14)', color: t.channel === 'email' ? C.teal : '#128c4a' }}>
          <Icon size={13} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: C.teal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
          {t.subject && <span style={{ display: 'block', fontSize: '0.66rem', color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</span>}
        </span>
        {t.attachments.length > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '0.64rem', fontWeight: 700, color: C.sub }}>
            <Paperclip size={11} /> {t.attachments.length}
          </span>
        )}
        {on && <Check size={14} color={C.teal} strokeWidth={3} />}
      </button>
    );
  };

  const isWhatsapp = (preview?.channel ?? selected?.channel) === 'whatsapp';

  const footer = whatsappUrl ? (
    <GlobalButton label="DONE" variant="secondary" icon={Check} onClick={onHide} />
  ) : (
    <>
      <GlobalButton label="Cancel" variant="secondary" icon={X} onClick={onHide} disabled={sending} />
      <GlobalButton label={isWhatsapp ? 'SAVE & CONTINUE' : 'SEND EMAIL'} icon={isWhatsapp ? MessageCircle : Send} onClick={send} loading={sending} disabled={!canSend} />
    </>
  );

  return (
    <ProposalDialog
      visible={visible}
      onHide={onHide}
      title={whatsappUrl ? 'Proposal saved' : 'Send a proposal'}
      subtitle={
        <>
          To <strong style={{ color: C.teal }}>{leadName}</strong>
        </>
      }
      icon={whatsappUrl ? Check : Send}
      busy={sending}
      maxWidth="640px"
      footer={footer}
    >
      {/* ── step 2 (WhatsApp only): open the chat ─────────────────────── */}
      {whatsappUrl ? (
        <>
          <Notice tone="ok">The proposal is saved on the lead. WhatsApp has not been sent yet — open the chat and press send there.</Notice>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setCountdown(null)} // opened by hand — stop the auto-open
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px', height: '42px', borderRadius: '9px', background: '#25D366', color: '#fff', fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.3px', textDecoration: 'none', boxShadow: '0 4px 12px rgba(37,211,102,0.35)' }}
          >
            <MessageCircle size={16} /> OPEN WHATSAPP{countdown !== null && countdown > 0 ? ` (${countdown}s)` : ''}
          </a>
          {countdown !== null && countdown > 0 && (
            <div style={{ textAlign: 'center', fontSize: '0.68rem', color: C.muted }}>
              Opening automatically in {countdown}s ·{' '}
              <button type="button" onClick={() => setCountdown(null)} style={{ background: 'none', border: 'none', padding: 0, color: C.teal, fontWeight: 700, fontSize: '0.68rem', cursor: 'pointer', textDecoration: 'underline' }}>
                cancel
              </button>
            </div>
          )}
        </>
      ) : loadingTemplates ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '26px', color: C.sub, fontSize: '0.78rem' }}>
          <Loader2 size={16} className="pp-spin" /> Loading templates…
        </div>
      ) : loadError ? (
        <Notice tone="error">{loadError}</Notice>
      ) : templates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '22px 12px' }}>
          <FileText size={26} color={C.muted} style={{ opacity: 0.5, marginBottom: '8px' }} />
          <div style={{ fontSize: '0.84rem', fontWeight: 700, color: C.teal, marginBottom: '4px' }}>No active templates</div>
          {isAdmin ? (
            <>
              <div style={{ fontSize: '0.75rem', color: C.sub, marginBottom: '14px' }}>Create a template once and anyone on your team can send it from any lead.</div>
              <GlobalButton
                label="MANAGE TEMPLATES"
                icon={FileText}
                onClick={() => {
                  onHide();
                  navigate('/proposals');
                }}
              />
            </>
          ) : (
            <div style={{ fontSize: '0.75rem', color: C.sub }}>There is nothing to send yet. Ask your admin to add a proposal template.</div>
          )}
        </div>
      ) : (
        <>
          {/* 1 · template */}
          <div>
            <label style={FIELD_LABEL}>1 · Template</label>
            {templates.length > 6 && (
              <input
                value={tplQuery}
                onChange={(e) => setTplQuery(e.target.value)}
                placeholder="Search templates…"
                style={{ width: '100%', height: '34px', padding: '0 10px', marginBottom: '7px', border: `1px solid ${G.line}`, borderRadius: '8px', outline: 'none', fontSize: '0.75rem', fontFamily: 'inherit', boxSizing: 'border-box' }}
              />
            )}
            <div style={{ maxHeight: '210px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '9px', paddingRight: '2px' }}>
              {grouped.length === 0 && <div style={{ fontSize: '0.74rem', color: C.muted, padding: '8px 2px' }}>No templates match.</div>}
              {grouped.map(([cat, list]) => (
                <div key={cat}>
                  <div style={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase', color: C.muted, margin: '0 0 4px 2px' }}>{cat}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>{list.map(templateCard)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* 2 · blanks */}
          {selected && (
            <div>
              <label style={FIELD_LABEL}>2 · Details</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                <GlobalInputField
                  label="Amount (₹)"
                  required={usesAmount}
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="e.g. 45000"
                  error={amountInvalid ? 'Enter an amount above 0.' : ''}
                  hint={!amountInvalid && typeof parsedAmount === 'number' ? fmtINR(parsedAmount) : usesAmount ? 'This template uses {{amount}}.' : 'Optional — saved with the proposal.'}
                  disabled={sending}
                />
                <GlobalDateField
                  label="Valid till"
                  required={usesValidTill}
                  value={validTill}
                  onChange={(v) => setValidTill((v as Date | null) ?? null)}
                  placeholder="dd/mm/yyyy"
                  minDate={new Date()}
                  showButtonBar
                  hint={usesValidTill ? 'This template uses {{valid_till}}.' : 'Optional — it shows as expired after this day.'}
                  disabled={sending}
                />
                {customNames.map((name) => (
                  <GlobalInputField
                    key={name}
                    label={varLabel(name)}
                    required
                    value={vars[name] ?? ''}
                    onChange={(e) => setVars((cur) => ({ ...cur, [name]: e.target.value }))}
                    placeholder={`Value for {{${name}}}`}
                    disabled={sending}
                  />
                ))}
              </div>
            </div>
          )}

          {/* 3 · preview */}
          {selected && (
            <div>
              <label style={{ ...FIELD_LABEL, display: 'flex', alignItems: 'center', gap: '7px' }}>
                3 · Preview {previewing && <Loader2 size={12} className="pp-spin" color={C.muted} />}
              </label>

              {previewError ? (
                <Notice tone="error">{previewError}</Notice>
              ) : preview ? (
                <div style={{ border: `1px solid ${G.line}`, borderLeft: `3px solid ${C.lime}`, borderRadius: '9px', background: G.shellBg, padding: '11px 13px', opacity: previewing ? 0.6 : 1, transition: 'opacity 0.15s' }}>
                  <div style={{ fontSize: '0.66rem', color: C.sub, marginBottom: '7px' }}>
                    {preview.channel === 'email' ? (
                      <>
                        <strong style={{ color: C.teal }}>To</strong> {preview.to_email}
                      </>
                    ) : (
                      <>
                        <strong style={{ color: '#128c4a' }}>WhatsApp</strong> {preview.to_phone}
                      </>
                    )}
                    {typeof preview.amount === 'number' && <span style={{ marginLeft: '11px' }}>Amount {fmtINR(preview.amount)}</span>}
                  </div>
                  {preview.subject && <div style={{ fontSize: '0.78rem', fontWeight: 700, color: C.teal, marginBottom: '6px' }}>{preview.subject}</div>}
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.76rem', color: '#1f2937', lineHeight: 1.6, maxHeight: '190px', overflowY: 'auto' }}>{preview.body}</div>
                  {preview.attachments.length > 0 && (
                    <div style={{ marginTop: '9px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {preview.attachments.map((a) => (
                        <a key={a.path} href={a.url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 9px', borderRadius: '20px', background: C.white, border: `1px solid ${G.line}`, color: C.teal, fontSize: '0.66rem', fontWeight: 700, textDecoration: 'none' }}>
                          <Paperclip size={11} /> {a.file_name}
                        </a>
                      ))}
                      <span style={{ fontSize: '0.63rem', color: C.muted, alignSelf: 'center' }}>added to the message as links</span>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: '0.74rem', color: C.muted, padding: '8px 2px' }}>Building the preview…</div>
              )}

              {missing.length > 0 && !previewError && (
                <div style={{ marginTop: '8px' }}>
                  <Notice tone="warn">Still needed: {missing.map(varLabel).join(', ')}. Fill {missing.length === 1 ? 'it' : 'them'} in above to send.</Notice>
                </div>
              )}
              {isWhatsapp && !previewError && (
                <div style={{ marginTop: '8px' }}>
                  <Notice tone="info">WhatsApp opens in the next step — you press send there. The proposal is saved on the lead first.</Notice>
                </div>
              )}
            </div>
          )}

          {sendError && <Notice tone="error">{sendError}</Notice>}
        </>
      )}
    </ProposalDialog>
  );
};

export default SendProposalModal;